import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getTask, doWork } from '../client';
import type { TaskResponse, TaskStatus } from '../client';
import { useSimulationId } from './useSimulationId';

export function useTask(taskId: string) {
  const simId = useSimulationId();

  const query = useQuery({
    queryKey: ['task', simId, taskId],
    queryFn: () => getTask({ path: { sim_id: simId, task_id: taskId }, throwOnError: true }).then(({ data }) => data),
    refetchInterval: 30_000,
  });

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (data: { requestBody: { effort: number } }) =>
      doWork({
        path: { sim_id: simId, task_id: taskId },
        body: data.requestBody,
        throwOnError: true,
      }).then(({ data }) => data),
    onMutate: async (newTask) => {
      await queryClient.cancelQueries({ queryKey: ['task', simId, taskId] });
      const taskBeforeUpdate = queryClient.getQueryData<TaskResponse>(['task', simId, taskId]);

      if (taskBeforeUpdate) {
        const completed_effort = Math.min(
          taskBeforeUpdate.completed_effort + newTask.requestBody.effort,
          taskBeforeUpdate.total_effort,
        );
        const status: TaskStatus = completed_effort === taskBeforeUpdate.total_effort ? 'completed' : 'in_progress';
        const optimisticUpdate: TaskResponse = {
          ...taskBeforeUpdate,
          completed_effort,
          status,
        };
        queryClient.setQueryData(['task', simId, taskId], optimisticUpdate);
      }
      return taskBeforeUpdate;
    },
    onError: (_, __, prev) => {
      queryClient.setQueryData(['task', simId, taskId], prev);
    },
    onSettled: (_) => {
      queryClient.invalidateQueries({ queryKey: ['task', simId, taskId] });
    },
  });

  return { mutate: mutation.mutate, ...query };
}
